import { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import styles from './AnadirPregunta.module.css'; 
import {useAuth} from '../../../Auth/AuthContext'; 

const AñadirPregunta = ({ testId, onBack, onPreguntaAdded, maxOrden = 0, isOpen }) => {
    const [formLoading, setFormLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [areas, setAreas] = useState([]);
    const [loadingAreas, setLoadingAreas] = useState(false);
    const {getToken} = useAuth(); 
    const token = getToken(); 

    const [formData, setFormData] = useState({
        pr_descripcion: '',
        pr_orden: maxOrden + 1,
        pr_requerida: true,
        pr_area_clave: ''
    });

    useEffect(() => {
        if (isOpen && testId) {
            cargarAreas();
        }
    }, [isOpen, testId]);

    const cargarAreas = async () => {
        setLoadingAreas(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/${testId}/areas`, { 
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const areasData = await response.json();
                setAreas(areasData);
            } else {
                console.error('Error al cargar áreas');
                setAreas([]);

                const errorData = await response.json().catch(() => ({}));
                await Swal.fire({
                    title: 'Error al cargar áreas',
                    text: errorData.message || 'No se pudieron cargar las áreas de resultado',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57',
                    customClass: {
                        popup: 'popupbonito'
                    }
                });
            }
        } catch (error) {
            console.error('Error al cargar áreas:', error);
            setAreas([]);
            await Swal.fire({
                title: 'Error de conexión',
                text: 'No se pudo conectar con el servidor para cargar las áreas',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });
        } finally {
            setLoadingAreas(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setFormData({
                pr_descripcion: '',
                pr_orden: maxOrden + 1,
                pr_requerida: true,
                pr_area_clave: ''
            });
            setErrors({});
        }
    }, [isOpen, maxOrden]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen && !formLoading) {
                onBack();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, formLoading, onBack]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.pr_descripcion.trim()) {
            newErrors.pr_descripcion = 'La descripción de la pregunta es obligatoria';
        }

        if (formData.pr_orden && formData.pr_orden < 1) {
            newErrors.pr_orden = 'El orden debe ser mayor a 0';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const mostrarErrorEspecifico = async (error, response) => {
        let titulo = 'Error';
        let mensaje = error.message || 'Ha ocurrido un error inesperado';
        let icono = 'error';

        try {
            const errorData = await response.json();

            switch (response.status) {
                case 400:
                    if (errorData.error === 'Faltan campos obligatorios') {
                        titulo = 'Campos Obligatorios';
                        mensaje = 'Por favor, completa todos los campos requeridos (descripción de la pregunta)';
                        icono = 'warning';
                    } else if (errorData.error === 'ID de test inválido') {
                        titulo = 'Test Inválido';
                        mensaje = 'El ID del test no es válido. Por favor, recarga la página e intenta nuevamente';
                        icono = 'warning';
                    } else if (errorData.error === 'Referencia no válida') {
                        titulo = 'Área de Resultado Inválida';
                        mensaje = 'El área de resultado seleccionada no existe. Por favor, selecciona una opción válida';
                        icono = 'warning';
                    } else if (errorData.error === 'Orden duplicado') {
                        titulo = 'Orden Duplicado';
                        mensaje = `Ya existe una pregunta con el orden ${formData.pr_orden} en este test. Por favor, elige un número diferente`;
                        icono = 'warning';
                    } else {
                        titulo = 'Error de Validación';
                        mensaje = errorData.message || 'Los datos enviados no son válidos';
                        icono = 'warning';
                    }
                    break;

                case 404:
                    if (errorData.error === 'Test no encontrado') {
                        titulo = 'Test No Encontrado';
                        mensaje = `El test con ID ${testId} no existe. Por favor, verifica que el test esté creado correctamente`;
                        icono = 'warning';
                    } else {
                        titulo = 'Recurso No Encontrado';
                        mensaje = errorData.message || 'El recurso solicitado no fue encontrado';
                        icono = 'warning';
                    }
                    break;
                    
                case 500:
                    if (errorData.error === 'Error interno del servidor') {
                        if (errorData.message.includes('verificar la existencia del test')) {
                            titulo = 'Error del Servidor';
                            mensaje = 'No se pudo verificar la existencia del test. Intenta nuevamente en unos momentos';
                        } else if (errorData.message.includes('calcular el orden')) {
                            titulo = 'Error del Servidor';
                            mensaje = 'No se pudo calcular el orden automático de la pregunta. Intenta especificar un orden manualmente';
                        } else if (errorData.message.includes('crear la pregunta')) {
                            titulo = 'Error del Servidor';
                            mensaje = 'No se pudo guardar la pregunta en la base de datos. Intenta nuevamente';
                        } else {
                            titulo = 'Error del Servidor';
                            mensaje = 'Ha ocurrido un error interno del servidor. Intenta nuevamente en unos momentos';
                        }
                        icono = 'error';
                    } else {
                        titulo = 'Error del Servidor';
                        mensaje = errorData.message || 'Error interno del servidor';
                        icono = 'error';
                    }
                    break;

                default:
                    titulo = `Error ${response.status}`;
                    mensaje = errorData.message || `Error ${response.status}: ${response.statusText}`;
                    icono = 'error';
            }
        } catch (parseError) {
            console.error('Error al parsear respuesta del servidor:', parseError);
            titulo = `Error ${response.status}`;
            mensaje = `Error ${response.status}: ${response.statusText}`;
            icono = 'error';
        }

        await Swal.fire({
            title: titulo,
            text: mensaje,
            icon: icono,
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#242b57',
            customClass: {
                popup: 'popupbonito'
            }
        });
    };

    const handleSubmitPregunta = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setFormLoading(true);

        try {
            const dataToSend = {
                pr_test_clave: testId,
                pr_descripcion: formData.pr_descripcion.trim(),
                pr_orden: parseInt(formData.pr_orden) || null,
                pr_requerida: formData.pr_requerida,
                pr_area_clave: formData.pr_area_clave ? parseInt(formData.pr_area_clave) : null
            };


            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/preguntas/crear-pregunta`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToSend)
            });

            if (!response.ok) {
                await mostrarErrorEspecifico(new Error(`Error ${response.status}`), response);
                return;
            }

            await Swal.fire({
                title: '¡Éxito!',
                text: 'La pregunta ha sido creada exitosamente',
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });

            if (onPreguntaAdded) {
                onPreguntaAdded();
            }

        } catch (error) {
            console.error('Error al crear pregunta:', error);

            await Swal.fire({
                title: 'Error de Conexión',
                text: 'No se pudo conectar con el servidor. Verifica tu conexión a internet e intenta nuevamente',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });
        } finally {
            setFormLoading(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !formLoading) {
            onBack();
        }
    };

    if (!isOpen) return null;

        return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
        <div className={styles.modalContainer}>
            {/* Header del Modal */}
            <div className={styles.modalHeader}>
            <h2>
                Nueva Pregunta
            </h2>
            <button
                className={styles.modalCloseBtn}
                onClick={onBack}
                disabled={formLoading}
                type="button"
            >
                <FaTimes />
            </button>
            </div>

            {/* Contenido del Modal */}
            <div className={styles.modalContent}>
            <div className={styles.preguntaForm}>
                <div className={styles.preguntaFormGroupWide}>
                <label htmlFor="pr_descripcion">
                    Descripción de la Pregunta
                </label>
                <input
                    id="pr_descripcion"
                    className={styles.inputForm}
                    name="pr_descripcion"
                    value={formData.pr_descripcion}
                    onChange={handleInputChange}
                    placeholder="Ej: ¿Te gusta vivir?"
                    disabled={formLoading}
                />
                {errors.pr_descripcion && (
                    <span className={styles.errorText}>{errors.pr_descripcion}</span>
                )}
                </div>

                <div className={styles.preguntaFormGrid}>
                <div className={styles.preguntaFormGroup}>
                    <label htmlFor="pr_orden">
                    Orden de la Pregunta
                    </label>
                    <input
                    type="number"
                    id="pr_orden"
                    name="pr_orden"
                    value={formData.pr_orden}
                    onChange={handleInputChange}
                    placeholder="Automático"
                    min="1"
                    max="999"
                    className={styles.inputForm}
                    disabled={formLoading}
                    />
                    {errors.pr_orden && (
                    <span className={styles.errorText}>{errors.pr_orden}</span>
                    )}
                </div>

                <div className={styles.preguntaFormGroup}>
                    <label htmlFor="pr_area_clave">
                    Área de Resultado
                    </label>
                    <select
                    id="pr_area_clave"
                    name="pr_area_clave"
                    value={formData.pr_area_clave}
                    onChange={handleInputChange}
                    className={styles.inputForm}
                    disabled={formLoading || loadingAreas}
                    >
                    <option value="">Sin área específica</option>
                    {areas.map(area => (
                        <option key={area.ar_clave} value={area.ar_clave}>
                        {area.ar_codigo} - {area.ar_nombre}
                        </option>
                    ))}
                    </select>
                    {loadingAreas && (
                    <small className={styles.formHelp}>Cargando áreas...</small>
                    )}
                </div>
                </div>

                <div className={styles.preguntaFormGroupWide}>
                <label className={styles.checkboxLabel}>
                    <input
                    type="checkbox"
                    name="pr_requerida"
                    checked={formData.pr_requerida}
                    onChange={handleInputChange}
                    disabled={formLoading}
                    />
                    <span className={styles.checkmark}></span>
                    <div>
                    <small className={styles.formHelp}>
                        Si marcas esta opción, los usuarios deberán responder esta pregunta antes de continuar.
                    </small>
                    </div>
                </label>
                </div>
            </div>
            </div>

            {/* Footer del Modal */}
            <div className={styles.modalFooter}>
            <div className={styles.preguntaFormActions}>
                <button
                type="submit"
                className={styles.btnSave}
                disabled={formLoading}
                onClick={handleSubmitPregunta}
                >
                {formLoading ? (
                    <>
                    <div className={styles.btnSpinner}></div>
                    Guardando...
                    </>
                ) : (
                    <>
                    Crear Pregunta
                    </>
                )}
                </button>
            </div>
            </div>
        </div>
        </div>
    );
};

export default AñadirPregunta;